import { FC, useEffect, useRef, useState, memo } from 'react';
import {
	Box,
	Image,
	List,
	ListItem,
	Skeleton,
	Text,
	Link,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IMessageProps {
	content: string;
	onImageClick?: (src: string) => void;
	onContentResize?: () => void;
	onImageLoad?: () => void;
}

const Message: FC<IMessageProps> = memo(
	({ content, onImageClick, onContentResize, onImageLoad }) => {
		const contentRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			if (!contentRef.current || !onContentResize) return;

			const resizeObserver = new ResizeObserver(() => {
				onContentResize();
			});

			resizeObserver.observe(contentRef.current);

			return () => {
				resizeObserver.disconnect();
			};
		}, [onContentResize]);

		return (
			<div ref={contentRef}>
				<ReactMarkdown
					remarkPlugins={[remarkGfm]}
					components={{
						img: ({ src, alt }) => (
							<LazyImage
								src={src as string}
								alt={alt}
								onImageClick={onImageClick}
								onImageLoad={onImageLoad}
							/>
						),
						a: ({ href, children }) => (
							<Link color="blue.500" href={href} isExternal fontSize="14px">
								{children}
							</Link>
						),
						p: ({ children }) => (
							<Text fontSize={{ base: '16px', md: '14px' }}>{children}</Text>
						),
						ul: ({ children }) => (
							<List spacing={2} styleType="disc" pl={4}>
								{children}
							</List>
						),
						ol: ({ children }) => (
							<List as="ol" spacing={2} styleType="decimal" pl={4}>
								{children}
							</List>
						),
						li: ({ children }) => <ListItem>{children}</ListItem>,
					}}
				>
					{content}
				</ReactMarkdown>
			</div>
		);
	},
);

Message.displayName = 'Message';

const LazyImage: FC<{
	src: string;
	alt?: string;
	onImageClick?: (src: string) => void;
	onImageLoad?: () => void;
}> = memo(({ src, alt, onImageClick, onImageLoad }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const imageRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		if (imageRef.current?.complete) {
			setIsLoaded(true);
			onImageLoad?.();
		}
	}, []);

	const handleLoad = () => {
		setIsLoaded(true);
		onImageLoad?.();
	};

	return (
		<Box
			position="relative"
			borderRadius="lg"
			overflow="hidden"
			cursor="pointer"
			maxW="200px"
			minW="200px"
		>
			{!isLoaded && <Skeleton h="150px" w="full" />}

			<Image
				ref={imageRef}
				src={src}
				alt={alt}
				onError={() => setIsLoaded(true)}
				onLoad={handleLoad}
				onClick={onImageClick ? () => onImageClick(src) : undefined}
				display={isLoaded ? 'block' : 'none'}
				borderRadius="lg"
				objectFit="cover"
				transition="opacity 0.3s ease-in-out"
			/>
		</Box>
	);
});

LazyImage.displayName = 'LazyImage';

export default Message;
